import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../Components/Layout';
import HeroSection from '../Components/Pricing/HeroSection';
import Plans from '../Components/Pricing/Plans';
import FAQ from '../Components/HomePage/FAQComponent';
import TitleAndDesc from '../Components/Helmet/TitleAndDesc';
import { API_KEY, BASE_URL, CLUSTERID } from '../api/config';

const pricingFAQ = [
  {
    question: 'Can I use DCDial for payments only?',
    answer:
      'Yes. The payment system works on its own — you do not need a paid software plan. Sign up on the free On Demand plan and start collecting payments with the DCDial processor, paying only per successful transaction. If you already use a software plan, the payment system works alongside it.',
  },
  {
    question: 'What does the payment system cost?',
    answer:
      'With the DCDial processor there is no monthly fee: cards are 3.5% + 50¢, bank payments are 2% (capped at $20), and wires are $20, each per successful transaction. Prefer your own processor? Plans start at $599/month plus a small percentage of revenue, based on your annual payment volume.',
  },
  {
    question: 'How do I choose the right plan?',
    answer:
      'Choose based on your number of users, active customer records, concurrency requirements, active campaigns, and active workflows. If you expect rapid growth, select a plan with enough capacity for the next 6–12 months.',
  },
  {
    question: 'What does concurrency mean?',
    answer:
      'Concurrency is the maximum number of outbound campaign actions the DCDial software can coordinate simultaneously. Actual performance may vary based on campaign configuration and other operational factors.',
  },
  {
    question: 'Can I change plans later?',
    answer:
      'Yes. You can upgrade as your team, records, campaigns, workflows, or capacity requirements grow. Downgrades take effect according to your subscription and billing terms.',
  },
  {
    question: 'How does annual billing work?',
    answer:
      'Annual plans provide a 20% discount compared with monthly billing. The displayed annual price is the effective monthly amount and the subscription is billed annually.',
  },
  {
    question: 'What if I need more than the published limits?',
    answer:
      'Talk to our sales team about a tailored configuration, implementation plan, or capacity requirements beyond the Everything plan.',
  },
];

const Pricing = () => {
  const [allPlansData, setAllPlansData] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const getPlans = async () => {
      try {
        const response = await axios.post(BASE_URL, {
          type: 'get_signup_plans',
          api_key: API_KEY,
          clusterid: CLUSTERID,
        });

        if (response.status === 200 && response.data.success === 1) {
          setAllPlansData(response.data.plans || []);
        }
      } catch {
        setAllPlansData([]);
      }
    };

    getPlans();
  }, []);

  return (
    <>
      <TitleAndDesc
        title="DCDial Pricing | Software Plans & Payment System"
        description="Compare DCDial software plans for CRM, campaigns, automation, and AI — or use the payment system on its own with no monthly fee. Pick what fits your team."
        isPageURL="https://dcdial.com/pricing"
        ogTitle="DCDial Software Plans and Payment System Pricing"
        ogDescription="Scalable DCDial software plans plus standalone payment processing — pay per transaction or bring your own processor."
      />
      <Layout>
        <HeroSection />
        <Plans allPlansData={allPlansData} />
        <FAQ data={pricingFAQ} isMargin={true} />

        <section className="mx-auto mb-[140px] max-w-[1040px] rounded-[24px] bg-LightPrimary px-10 py-14 text-center max_md:mx-10 max_sm:mx-5 max_sm:mb-[70px] max_sm:px-5 max_sm:py-10">
          <h2 className="text-text3Xl font-DMSansFont font-bold text-white max_sm:text-text2xl">
            Find the right fit for your operation
          </h2>
          <p className="mx-auto mt-4 max-w-[680px] text-textSmall leading-6 text-white/75">
            Compare software capacity, start with payments only, or combine both—and scale as your team and customer operations grow.
          </p>
          <div className="mt-7 flex justify-center gap-4 max_sm:flex-col">
            <a
              href="#plan-limit-definitions"
              className="rounded-[10px] bg-ButtonPrimary px-6 py-3 text-textSmall font-bold text-white hover:bg-ButtonPrimaryHover"
            >
              Compare Plans
            </a>
            <Link
              to="/contact/get-in-touch"
              className="rounded-[10px] border border-white px-6 py-3 text-textSmall font-bold text-white hover:bg-white/10"
            >
              Talk to Sales
            </Link>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Pricing;
